import { useMount } from 'react-use';
import { useEffect, useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'next-i18next/pages';
import { GetListingResponseStatus } from '@pkgs/api/index';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { alertVariants } from '@ev-tech/ui-core-components/Alert';
import { useRegisterChatbotContext } from '@ev-tech/search-modules-chatbot';

import { getPropertyAvailableLanguageMessage } from '@/utils/propertyDataUtils';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useExposePageProps } from '@/hooks/useExposePageProps';

import { useSettings } from './useSettings';
import { useLastVisitedProperties } from './useLastVisitedProperties';
import { useListing } from './queries/useListing';

export const useExposePageMountEffects = () => {
  const { showMessage } = useStatusMessage();
  const { language } = useSettings();
  const {
    data: { listing, status },
  } = useListing();
  const { privateOfficePassword, listingId, pageVariant } = useExposePageProps();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const { t: tPrivateOffice } = useTranslation(TRANSLATION_NAMESPACE.private_office);
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);

  useLastVisitedProperties(listingId);

  const chatbotContext = useMemo(
    () => ({
      pageType: pageVariant,
      ...(listing?.profile.title && { title: listing?.profile.title }),
      ...(listing?.profile.description && { description: listing?.profile.description }),
      ...(listing?.profile.locationDescription && { locationDescription: listing?.profile.locationDescription }),
    }),
    [listing, pageVariant],
  );

  useRegisterChatbotContext(chatbotContext);

  useEffect(() => {
    // Show snackbar if property is not available in the language that user has chosen in GlobalSettings
    if (listing?.languages && !listing.languages.includes(language)) {
      showMessage(getPropertyAvailableLanguageMessage(t, language, listing.languages), {
        alertType: alertVariants.info,
      });
    }

    // we only want to run this effect once on mount or when the language changes
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useMount(() => {
    const secretAccessId = searchParams?.get('secretAccessId');
    const promotedByParam = searchParams?.get('promotedBy');
    if (secretAccessId && status === GetListingResponseStatus.INVALID_SECRET_ACCESS_ID) {
      showMessage(tPrivateOffice('search.expose.label.invalidAccessId'), { alertType: alertVariants.error });
    }

    if (privateOfficePassword && status === GetListingResponseStatus.INVALID_PASSWORD) {
      showMessage(tPrivateOffice('search.expose.label.invalidPassword'), { alertType: alertVariants.error });
    }

    if (promotedByParam && listing?.agent && listing.agent.id !== promotedByParam) {
      const params = new URLSearchParams(searchParams?.toString() ?? '');
      params.delete('promotedBy');
      const targetUrl = `${pathname}${params.size ? `?${params.toString()}` : ''}`;
      router.replace(targetUrl, { scroll: false });
    }
  });
};
